import { validatePasswordStrength, getPasswordStrengthLabel, PasswordRequirements, STANDARD_PASSWORD_REQUIREMENTS } from '@/lib/security';
import { cn } from '@/lib/utils';
import { Check, X } from 'lucide-react';

interface PasswordStrengthMeterProps {
  password: string;
  showFeedback?: boolean;
  requirements?: PasswordRequirements;
}

export function PasswordStrengthMeter({ 
  password, 
  showFeedback = true,
  requirements = STANDARD_PASSWORD_REQUIREMENTS 
}: PasswordStrengthMeterProps) {
  if (!password) {
    return null;
  }

  const strength = validatePasswordStrength(password, requirements);
  const { label, color } = getPasswordStrengthLabel(strength.score);

  // Only show the minimum length requirement for standard users
  const checks = [
    { 
      label: `${requirements.minLength}+ characters`, 
      met: password.length >= requirements.minLength,
      required: true
    },
  ].filter(c => c.required !== false);

  return (
    <div className="space-y-2">
      {/* Strength bar */}
      <div className="flex gap-1">
        {[0, 1, 2, 3].map((index) => (
          <div
            key={index}
            className={cn(
              'h-1.5 flex-1 rounded-full transition-colors',
              index < strength.score
                ? strength.score >= 3
                  ? 'bg-green-500'
                  : strength.score >= 2
                  ? 'bg-yellow-500'
                  : 'bg-destructive'
                : 'bg-muted'
            )}
          />
        ))}
      </div>

      {/* Label */}
      <div className="flex items-center justify-between text-xs">
        <span className={cn('font-medium', color)}>{label}</span>
        {strength.meetsAllRequirements ? (
          <span className="text-green-500 flex items-center gap-1">
            <Check className="h-3 w-3" /> All requirements met
          </span>
        ) : (
          <span className="text-destructive">Requirements not met</span>
        )}
      </div>

      {/* Requirement checklist */}
      {showFeedback && (
        <div className="grid grid-cols-2 gap-1 text-xs">
          {checks.map((check, index) => (
            <div 
              key={index} 
              className={cn(
                'flex items-center gap-1',
                check.met ? 'text-green-500' : 'text-muted-foreground'
              )}
            >
              {check.met ? (
                <Check className="h-3 w-3" />
              ) : (
                <X className="h-3 w-3" />
              )}
              <span>{check.label}</span>
            </div>
          ))}
        </div>
      )}

      {/* Additional improvement suggestions */}
      {showFeedback && strength.feedback.length > 0 && strength.score < 3 && (
        <ul className="text-xs text-muted-foreground space-y-0.5 mt-1">
          {strength.feedback.slice(0, 2).map((tip, index) => (
            <li key={index}>• {tip}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
